import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { fetchPutFormalOffersCreditFacilityConfiguration } from '@iwoca/lapi-client/edge';
import { Button, Card, Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { CalculatePotentialSavings } from './CalculatePotentialSavings';
import { InterestRate } from './InterestRate';
import { NewOutstandingBalance } from './NewOutstandingBalance';
import { RepaymentSummary } from './RepaymentSummary';
import { SeeNewSchedule } from './SeeNewSchedule';
import { SeeRateDetails } from './SeeRateDetails';
import { useStateKey } from '../../../../../../iwQueryClient/useStateKey';
import { getLoanDurationInMonths } from '../../../../../myOffers/MyOffers.helpers';
import { isSelectedAmountValid } from '../../../../../ui/ChooseAmountInputWithSlider/isSelectedAmountValid';

export const NewLoanSummary = ({
    selectedAmount,
    selectedOffer,
    outstandingBalance,
    isPartialDrawdown
}: {
    selectedAmount: number | null;
    selectedOffer: TFormalOffer | null;
    outstandingBalance: number;
    isPartialDrawdown?: boolean;
}) => {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();
    const history = useHistory();
    const drawdownAmount = selectedAmount ?? selectedOffer?.available_cash!;

    const createOfferConfigurationMutation = useMutation({
        mutationKey: ['createOfferConfiguration'],
        mutationFn: ({
            offerId,
            drawdownAmount
        }: {
            offerId: string;
            drawdownAmount: number;
        }) =>
            fetchPutFormalOffersCreditFacilityConfiguration({
                stateKey,
                offerId,
                body: {
                    data: {
                        cash_amount: drawdownAmount
                    }
                }
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [
                    'fetchGetFormalOffersLatestOfferConfiguration',
                    stateKey
                ]
            })
    });

    const duration = selectedOffer
        ? getLoanDurationInMonths(selectedOffer.duration!)
        : '--';

    const handleClickContinueToFinalChecks = async () => {
        if (!selectedOffer) return;

        const offerId = selectedOffer.offer_id!;

        await createOfferConfigurationMutation.mutateAsync({
            offerId,
            drawdownAmount: drawdownAmount || 0
        });

        history.push(
            maintainStateKey({
                pathname: '/funding-requirements/',
                search: `?offerId=${selectedOffer.offer_id!}&amount=${drawdownAmount}`
            })
        );
    };

    return (
        <Card className="space-y-l min-l:max-w-[375px] min-l:p-xl">
            <h3 className="m-0">
                <FormattedMessage
                    id="_.topup_choice.approved_new_loan_summary"
                    defaultMessage="New loan summary"
                />
            </h3>
            <div className="w-full space-y-m">
                <NewOutstandingBalance
                    drawdownAmount={drawdownAmount}
                    outstandingBalance={outstandingBalance}
                />
                <RepaymentSummary
                    drawdownAmount={drawdownAmount}
                    selectedOffer={selectedOffer}
                />
                <div>
                    <div className="flex items-center justify-between">
                        <Text>
                            <FormattedMessage
                                id="_.topup_choice.approved_interest_rate"
                                defaultMessage="Interest rate per 30 days"
                            />
                        </Text>
                        <Text className="font-med">
                            {selectedOffer ? (
                                <InterestRate
                                    drawdownAmount={drawdownAmount}
                                    selectedOffer={selectedOffer}
                                />
                            ) : (
                                '--%'
                            )}
                        </Text>
                    </div>
                    {selectedOffer && (
                        <SeeRateDetails
                            drawdownAmount={drawdownAmount}
                            selectedOffer={selectedOffer}
                            isPartialDrawdown={isPartialDrawdown}
                        />
                    )}
                </div>
                <div>
                    <div className="flex items-center justify-between">
                        <Text>
                            <FormattedMessage
                                id="_.topup_choice.approved_remaining_term"
                                defaultMessage="Remaining term"
                            />
                        </Text>
                        <Text className="font-med">
                            <FormattedMessage
                                id="_.topup_choice.approved_remaining_term_months"
                                defaultMessage="{duration} months"
                                values={{ duration }}
                            />
                        </Text>
                    </div>
                    {selectedOffer && (
                        <SeeNewSchedule
                            drawdownAmount={drawdownAmount}
                            selectedOffer={selectedOffer}
                        />
                    )}
                </div>
            </div>

            {selectedOffer && (
                <CalculatePotentialSavings
                    selectedOffer={selectedOffer}
                    drawdownAmount={drawdownAmount}
                />
            )}

            <Button
                variant="success"
                className="w-full"
                disabled={!isSelectedAmountValid(drawdownAmount, selectedOffer)}
                onClick={handleClickContinueToFinalChecks}
                loading={createOfferConfigurationMutation.isLoading}
            >
                <FormattedMessage
                    id="_.topup_choice.continue_to_final_checks"
                    defaultMessage="Continue to final checks"
                />
            </Button>
        </Card>
    );
};
